import { Formik } from 'formik';
import React, { useState } from 'react';
import {
  IbanForm,
  IbanImage,
} from '../../pages/Bankreader/BankreaderStyledComponents';
import {
  FormFloating,
  FormInput,
  FormInputLabel,
  FormOption,
  ValidationError,
} from '../../styles/FormStyledComponents';
import {
  MainButton,
  Subtitle,
  Title,
} from '../../styles/GlobalStyledComponents';
import piggyBank from '../../assets/images/iban/piggy-bank.svg';
import CreditProposalService from '../../services/CreditProposal';
import { useCreditProposalResult } from '../../contexts/CreditProposalResultContext';
import { CreditProposalUpdate } from '../../interfaces/CreditProposalResult';
import { useNavigate } from 'react-router-dom';
import { ROUTES_PATH } from '../../constants';
import { DecisionStatus } from '../../interfaces/DecisionEngine';
import AnalyticsService from '../../services/Analytics';
import { DenialStep } from '../../interfaces/Analytics';
import { useCustomerClassification } from '../../contexts/CustomerClassificationContext';

interface IbanSelectionProps {
  ibanList: string[];
  loadingEvent: (data: boolean) => void;
}

const IbanSelection: React.FC<IbanSelectionProps> = ({
  ibanList,
  loadingEvent,
}) => {
  let navigate = useNavigate();
  const { creditProposalResult } = useCreditProposalResult();
  const { customerClassification } = useCustomerClassification();
  let decisionStatus: any = { signatureReady: false, isDecisionPending: true };

  const handleIbanSelect = async (e: { iban: string }) => {
    loadingEvent(true);
    if (creditProposalResult) {
      try {
        const data: CreditProposalUpdate[] = [
          {
            op: 'replace',
            path: 'customer/iban',
            value: e.iban,
          },
        ];
        await CreditProposalService.update(
          data,
          creditProposalResult?.credit_proposal_id
        );
        if (customerClassification.require_documentation && !creditProposalResult.ocr_id) {
          navigate(ROUTES_PATH.OCR);
        } else {
          await getPADecision(creditProposalResult.credit_proposal_id, 0);
        }
      } catch (error: any) {
        loadingEvent(false);
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    } else {
      loadingEvent(false);
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const getPADecision = async (creditProposalId: string, counter: number) => {
    loadingEvent(true);
    counter++;
    try {
      const decisionResp = await CreditProposalService.getDecision(
        creditProposalId
      );

      if (decisionResp) {
        loadingEvent(false);
        switch (decisionResp.decision) {
          case DecisionStatus.APPROVED:
          case DecisionStatus.NOT_NECESSARY:
            decisionStatus = { signatureReady: true, isDecisionPending: false };
            navigate(ROUTES_PATH.SIGNATURE, { state: { decisionStatus } });
            break;
          case DecisionStatus.NO_DECISION:
          case DecisionStatus.PENDING:
            decisionStatus = { signatureReady: true, isDecisionPending: true };
            navigate(ROUTES_PATH.SIGNATURE, { state: { decisionStatus } });
            break;
          case DecisionStatus.DENIED:
            AnalyticsService.sendLoanRefused({denial_step: DenialStep.PAC});
            navigate(ROUTES_PATH.DENIAL);
            break;
          case DecisionStatus.MISSING_DATA:
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
            break;
          default:
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
            break;
        }
      }
    } catch (error) {
      if (`${error}`.includes('409') && counter <= 11) {
        setTimeout(() => {
          getPADecision(creditProposalId, counter);
        }, 3000);
      } else {
        loadingEvent(false);
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };

  return (
    <Formik
      initialValues={{
        iban: ibanList[0],
      }}
      onSubmit={handleIbanSelect}
      enableReinitialize
    >
      {({ errors, touched }) => (
        <IbanForm id="iban-selection-form">
          <div>
            <Title>¡Ya casi estamos!</Title>
            <Subtitle>
              Selecciona la cuenta por la que se van a girar los recibos:
            </Subtitle>
          </div>
          <FormFloating id="iban-floating">
            <FormInput
              id="iban-input"
              autoComplete="off"
              key="iban"
              name="iban"
              component="select"
            >
              {ibanList?.map((iban: string) => (
                <FormOption key={iban} value={iban}>
                  {iban}
                </FormOption>
              ))}
            </FormInput>
            <FormInputLabel
              id="iban-label"
              htmlFor="iban"
              $error={errors.iban && touched.iban ? 'error' : ''}
            >
              Número de cuenta
            </FormInputLabel>
            {errors.iban && touched.iban && (
              <ValidationError>{errors.iban}</ValidationError>
            )}
          </FormFloating>
          <IbanImage id="iban-image" src={piggyBank} alt="iban-image" />
          <MainButton id="iban-selection-continue-btn" type="submit">
            Continuar
          </MainButton>
        </IbanForm>
      )}
    </Formik>
  );
};

export default IbanSelection;
